import { FamilyMember } from "./familyTreeUtils";

/**
 * Get the parents of a family member, handling both old (children) and new (parents) formats
 */
export function getParents(member: FamilyMember): FamilyMember[] {
  // Prefer the new 'parents' field if it exists
  if (member.parents && member.parents.length > 0) {
    return member.parents;
  }

  // Fall back to the old 'children' field (which actually contains parents)
  if (member.children && member.children.length > 0) {
    return member.children;
  }

  return [];
}

/**
 * Set the parents of a family member using the new format
 */
export function setParents(
  member: FamilyMember,
  parents: FamilyMember[]
): FamilyMember {
  return {
    ...member,
    parents: parents,
    // Remove the old children field to avoid confusion
    children: undefined,
  };
}

/**
 * Migrate a single family member from old format to new format
 */
export function migrateFamilyMember(member: FamilyMember): FamilyMember {
  if (member.children && !member.parents) {
    // Migrate children to parents
    const migratedMember: FamilyMember = {
      ...member,
      parents: member.children.map(migrateFamilyMember), // Recursively migrate
      children: undefined, // Remove the old field
    };
    return migratedMember;
  }

  // If already migrated or no children, just recursively migrate parents
  if (member.parents) {
    return {
      ...member,
      parents: member.parents.map(migrateFamilyMember),
      children: undefined, // Ensure old field is removed
    };
  }

  return member;
}

/**
 * Migrate an array of family members from old format to new format
 */
export function migrateFamilyTree(familyTree: FamilyMember[]): FamilyMember[] {
  return familyTree.map(migrateFamilyMember);
}

/**
 * Check if a family member or tree has been migrated to the new format
 */
export function isMigrated(member: FamilyMember): boolean {
  // A member is considered migrated if it has parents field and no children field
  // OR if it has neither (leaf node)
  return (
    (member.parents !== undefined && member.children === undefined) ||
    (member.parents === undefined && member.children === undefined)
  );
}

/**
 * Check if an entire family tree has been migrated
 */
export function isFamilyTreeMigrated(familyTree: FamilyMember[]): boolean {
  const checkMemberRecursively = (member: FamilyMember): boolean => {
    if (!isMigrated(member)) {
      return false;
    }

    const parents = getParents(member);
    return parents.every(checkMemberRecursively);
  };

  return familyTree.every(checkMemberRecursively);
}

/**
 * Create a backup of family tree data before migration
 */
export function createMigrationBackup(familyTree: FamilyMember[]): {
  timestamp: string;
  originalData: FamilyMember[];
  version: string;
} {
  return {
    timestamp: new Date().toISOString(),
    originalData: JSON.parse(JSON.stringify(familyTree)), // Deep copy
    version: "pre-parents-migration",
  };
}
